import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { useState } from 'react';
import { AddSectionButton, AddSectionOverlay, iconByTone, severityLabel, toneStyles } from '@/components/block-primitives';
import type { CalloutBlockConfig } from './artifact-block-types';
import EditableText from './editable-text';
type ArtifactCalloutBlockProps = {
  config: CalloutBlockConfig;
  editable?: boolean;
  onChange?: (next: CalloutBlockConfig) => void;
};
const ArtifactCalloutBlock = t0 => {
  const $ = _c(50);
  const {
    config,
    editable: t1,
    onChange
  } = t0;
  const editable = t1 === undefined ? false : t1;
  const [addTitle, setAddTitle] = useState(false);
  const [addNote, setAddNote] = useState(false);
  let t2;
  if ($[0] !== config || $[1] !== onChange) {
    t2 = next => onChange?.({
      ...config,
      ...next
    });
    $[0] = config;
    $[1] = onChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const patch = t2;
  const styles = toneStyles[config.severity];
  const surface = config.severity === "neutral" ? "border-tertiary bg-primary" : styles.surface;
  const Icon = iconByTone[config.severity];
  const label = `${severityLabel[config.severity]} callout${config.title ? `: ${config.title}` : ""}`;
  const showTitle = !!config.title || addTitle;
  const showNote = !!config.note || addNote;
  let t3;
  if ($[3] !== editable || $[4] !== showTitle) {
    t3 = editable && !showTitle && <AddSectionOverlay position="top"><AddSectionButton label="Add a title" onClick={() => setAddTitle(true)} /></AddSectionOverlay>;
    $[3] = editable;
    $[4] = showTitle;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== editable || $[7] !== showNote) {
    t4 = editable && !showNote && <AddSectionOverlay position="bottom"><AddSectionButton label="Add a note" onClick={() => setAddNote(true)} /></AddSectionOverlay>;
    $[6] = editable;
    $[7] = showNote;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== surface) {
    t5 = cx("flex items-start gap-3 rounded-lg border p-4", surface);
    $[9] = surface;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== Icon || $[12] !== styles.icon) {
    t6 = <span aria-hidden="true" className="flex h-5 w-5 shrink-0 items-center justify-center"><Icon color={styles.icon} /></span>;
    $[11] = Icon;
    $[12] = styles.icon;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== addTitle || $[15] !== config.title || $[16] !== editable || $[17] !== patch || $[18] !== showTitle || $[19] !== styles.value) {
    t7 = showTitle && <EditableText as="div" autoFocus={addTitle} block={true} className={cx("text-label-2", styles.value)} editable={editable} label="Callout title" onSubmit={title => patch({
      title
    })} onEmptyBlur={() => setAddTitle(false)} optional={true} placeholder="Add a title" value={config.title ?? ""} />;
    $[14] = addTitle;
    $[15] = config.title;
    $[16] = editable;
    $[17] = patch;
    $[18] = showTitle;
    $[19] = styles.value;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const t8 = config.title && "mt-1";
  let t9;
  if ($[21] !== t8) {
    t9 = cx("text-body-2 text-primary", t8);
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== patch) {
    t10 = body => patch({
      body
    });
    $[23] = patch;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== config.body || $[26] !== editable || $[27] !== t10 || $[28] !== t9) {
    t11 = <EditableText as="div" block={true} className={t9} editable={editable} label="Callout body" multiline={true} onSubmit={t10} value={config.body} />;
    $[25] = config.body;
    $[26] = editable;
    $[27] = t10;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== addNote || $[31] !== config.note || $[32] !== editable || $[33] !== patch || $[34] !== showNote || $[35] !== styles.value) {
    t12 = showNote && <EditableText as="div" autoFocus={addNote} block={true} className={cx("mt-2 text-caption-4", styles.value)} editable={editable} label="Callout note" onSubmit={note => patch({
      note
    })} onEmptyBlur={() => setAddNote(false)} optional={true} placeholder="Add a note" value={config.note ?? ""} />;
    $[30] = addNote;
    $[31] = config.note;
    $[32] = editable;
    $[33] = patch;
    $[34] = showNote;
    $[35] = styles.value;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t11 || $[38] !== t12 || $[39] !== t7) {
    t13 = <div className="min-w-0 flex-1">{t7}{t11}{t12}</div>;
    $[37] = t11;
    $[38] = t12;
    $[39] = t7;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== label || $[42] !== t13 || $[43] !== t5 || $[44] !== t6) {
    t14 = <section aria-label={label} className={t5}>{t6}{t13}</section>;
    $[41] = label;
    $[42] = t13;
    $[43] = t5;
    $[44] = t6;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== t14 || $[47] !== t3 || $[48] !== t4) {
    t15 = <div className="group relative">{t3}{t4}{t14}</div>;
    $[46] = t14;
    $[47] = t3;
    $[48] = t4;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  return t15;
};
export default ArtifactCalloutBlock;